import { AppSelect } from '~/components/new/app-ui/app-select'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { resolveTabs } from '~/components/new/app-ui/app-tabs-state'
import { ScrollArea, ScrollAreaContent } from '~/components/ui/scroll-area'
import { useSimulatorSelectorOptionsQuery } from '~/lib/logistics-simulations-query'
import { cn } from '~/lib/utils'
import { WEEK_DAY_OPTIONS } from '~/lib/week-days'
import type {
  LogisticsSearch,
  LogisticsTabValue,
} from '~/routes/_authenticated/logistics/-search-schema'
import { useAuthorizedTabs } from '~/utils/rbacTabs'
import {
  LOGISTICS_FILTER_CHIP,
  LOGISTICS_NAV_BAR_ONE_LINE,
  LOGISTICS_NAV_FILTERS_ONE_LINE,
  LOGISTICS_NAV_TABS_SCROLL,
  LOGISTICS_SELECT_CONTENT_CLASS,
} from './logistics-styles'
import { filterOptionsWithCurrent } from './logistics-filter-options'
import { QueryError } from './logistics-shared'
import { logisticsTabDataNeeds } from './logistics-tabs-state'
import { LOGISTICS_TABS } from './tabs'

/** Onglets résolus contre les droits (cf. `resolveTabs`). */
export const useLogisticsTabs = (requestedTab: LogisticsTabValue) =>
  resolveTabs(LOGISTICS_TABS, useAuthorizedTabs(LOGISTICS_TABS), requestedTab)

// Largeur bornée, valeur tronquée : un nom de cuisine centrale est libre, et sur une ligne les
// filtres ne rétrécissent pas (cf. `LOGISTICS_NAV_FILTERS_ONE_LINE`) ; les onglets paieraient
// l'excédent.
const logisticsSelectClassName = cn(
  LOGISTICS_FILTER_CHIP,
  'max-w-60 data-placeholder:text-primary-600 [&>span]:block [&>span]:min-w-0 [&>span]:truncate',
)

/**
 * Barre de la page Logistique (maquette) : onglets du module, puis les filtres globaux du
 * simulateur quand son onglet est actif. Commune à la liste et au détail d'une tournée, où elle
 * mène à la liste (`onSearchChange` décide de la navigation).
 */
export function LogisticsNavBar({
  search,
  onSearchChange,
}: {
  search: LogisticsSearch
  onSearchChange: (patch: Partial<LogisticsSearch>) => void
}) {
  const { tabs, activeTab } = useLogisticsTabs(search.tab)
  const showSimulator = logisticsTabDataNeeds(activeTab).selectorOptions
  // Options des sélecteurs, lues à part sur une clé stable (cf. `useSimulatorSelectorOptionsQuery`) ;
  // leur erreur éventuelle s'affiche sous la barre, sur la liste comme sur le détail.
  const selectorOptionsQuery = useSimulatorSelectorOptionsQuery(showSimulator)
  const kitchenFilter = filterOptionsWithCurrent(
    (selectorOptionsQuery.data?.centralKitchens ?? []).map((kitchen) => ({
      label: kitchen.label,
      value: kitchen.id,
    })),
    search.kitchen,
    selectorOptionsQuery.status,
    (id, status) => (status === 'error' ? `Atelier n° ${id}` : `Atelier introuvable (n° ${id})`),
  )
  const departmentFilter = filterOptionsWithCurrent(
    (selectorOptionsQuery.data?.departmentCodes ?? []).map((code) => ({
      label: code,
      value: code,
    })),
    search.department,
    selectorOptionsQuery.status,
    (code) => code,
  )

  return (
    <>
      {/* Une ligne dès que la barre est assez large : sa largeur réelle, pas celle de l'écran (cf.
          `LOGISTICS_NAV_BAR_ONE_LINE`). */}
      <div className="@container">
        <div
          className={cn(
            'mb-2 flex flex-wrap items-center gap-4 rounded-3xl border border-black/6 bg-background-100 p-2',
            LOGISTICS_NAV_BAR_ONE_LINE,
          )}
        >
          <ScrollArea scrollFade className={LOGISTICS_NAV_TABS_SCROLL}>
            <ScrollAreaContent>
              <AppTabs
                tabs={tabs}
                variant="pill"
                value={activeTab}
                onTabChange={(tab) => onSearchChange({ tab })}
              />
            </ScrollAreaContent>
          </ScrollArea>

          {showSimulator && (
            <div
              className={cn(
                'flex flex-wrap items-center gap-2 self-stretch border-l border-black/6 pl-4',
                LOGISTICS_NAV_FILTERS_ONE_LINE,
              )}
            >
              <AppSelect
                placeholder="Cuisines centrales"
                placeholderOptionLabel="Toutes les cuisines centrales"
                contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
                options={kitchenFilter.options}
                value={kitchenFilter.value}
                onChange={(value) => onSearchChange({ kitchen: value ?? undefined })}
                className={logisticsSelectClassName}
                valueTitle
              />
              <AppSelect
                placeholder="Départements"
                placeholderOptionLabel="Tous les départements"
                contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
                options={departmentFilter.options}
                value={departmentFilter.value}
                onChange={(value) => onSearchChange({ department: value ?? undefined })}
                className={logisticsSelectClassName}
                valueTitle
              />
              <AppSelect
                // Les tournées qui partent ce jour-là (#903).
                placeholder="Jours"
                placeholderOptionLabel="Tous les jours"
                contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
                options={WEEK_DAY_OPTIONS}
                value={search.day ?? null}
                onChange={(value) => onSearchChange({ day: value ?? undefined })}
                className={logisticsSelectClassName}
                valueTitle
              />
            </div>
          )}
        </div>
      </div>
      <QueryError
        error={showSimulator ? selectorOptionsQuery.error : null}
        label="Options des filtres indisponibles — les sélecteurs Cuisines centrales et Départements restent vides"
      />
    </>
  )
}
